/**
 * Audit React Query Hooks
 *
 * TanStack Query hooks for audit operations
 */

import { useQuery } from '@tanstack/react-query'
import type { AuditLogFilters, AuditResourceType } from '@/domain/audit'
import * as auditService from './audit.service'
import { usePermiso } from '@/application/auth'

// =============================================================================
// Query Keys
// =============================================================================

export const auditKeys = {
  all: ['audit'] as const,
  logs: () => [...auditKeys.all, 'logs'] as const,
  logsList: (filters?: AuditLogFilters) => [...auditKeys.logs(), filters] as const,
  resourceHistory: (type: AuditResourceType, id: string) =>
    [...auditKeys.all, 'resource', type, id] as const,
  userActivity: (userId: string) => [...auditKeys.all, 'user', userId] as const,
  securityAlerts: (options?: { ipAddress?: string; dateFrom?: string; limit?: number }) =>
    [...auditKeys.all, 'security', options] as const,
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Get paginated/filtered audit logs
 */
export function useAuditLogs(filters?: AuditLogFilters) {
  return useQuery({
    queryKey: auditKeys.logsList(filters),
    queryFn: () => auditService.getAuditLogs(filters),
    staleTime: 1000 * 30, // 30 seconds
  })
}

/**
 * Get audit history for a specific resource
 */
export function useResourceHistory(
  resourceType: AuditResourceType | undefined,
  resourceId: string | undefined
) {
  // La ruta exige `audit:view:all`, que solo tienen quienes auditan. El
  // radiologo abria el detalle del estudio y esta consulta devolvia 403 en
  // cada carga: ruido en la consola por una seccion que ni siquiera puede ver.
  const puede = usePermiso('audit:view:all')
  return useQuery({
    queryKey: auditKeys.resourceHistory(resourceType!, resourceId!),
    queryFn: () => auditService.getResourceHistory(resourceType!, resourceId!),
    enabled: !!resourceType && !!resourceId && puede,
    staleTime: 1000 * 60, // 1 minute
  })
}

/**
 * Get activity history for a specific user
 */
export function useUserActivity(userId: string | undefined) {
  return useQuery({
    queryKey: auditKeys.userActivity(userId!),
    queryFn: () => auditService.getUserActivity(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60,
  })
}

/**
 * Get security alerts
 */
export function useSecurityAlerts(
  options?: { ipAddress?: string; dateFrom?: string; limit?: number }
) {
  return useQuery({
    queryKey: auditKeys.securityAlerts(options),
    queryFn: () => auditService.getSecurityAlerts(options),
    staleTime: 1000 * 30,
  })
}
